/* =====================================================================
 * Modal — 공통 모달 컴포넌트 (테마 기반)
 * ===================================================================== */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { X, Pin, PinOff } from 'lucide-react';

/* ------------------------------------------------------------------ */
/* Types                                                               */
/* ------------------------------------------------------------------ */

type ResizeDir = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

const RESIZE_DIRS: ResizeDir[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

const CURSOR_MAP: Record<ResizeDir, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

const OPACITY_MIN = 35;
const OPACITY_MAX = 100;

function toRgbaWithAlpha(color: string, alpha: number): string {
  const normalized = color.trim();

  if (normalized.startsWith('#')) {
    const hex = normalized.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (hex.length >= 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }

  const rgb = normalized.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(',').map((v) => v.trim());
    if (parts.length >= 3) {
      const r = Number(parts[0]);
      const g = Number(parts[1]);
      const b = Number(parts[2]);
      if ([r, g, b].every(Number.isFinite)) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }
  }

  return normalized;
}

/* ------------------------------------------------------------------ */
/* Animations                                                          */
/* ------------------------------------------------------------------ */

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const modalSlideIn = keyframes`
  from { opacity: 0; transform: translateY(16px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const hideScrollbarCss = css`
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Overlay = styled.div<{
  $dimmed: boolean;
  $passthrough: boolean;
  $centerPadLeft: number;
  $centerPadRight: number;
  $mountMode: 'viewport' | 'parent';
  $zIndex: number;
}>`
  position: ${({ $mountMode }) => ($mountMode === 'parent' ? 'absolute' : 'fixed')};
  inset: 0;
  box-sizing: border-box;
  padding-left: ${({ $centerPadLeft }) => `${$centerPadLeft}px`};
  padding-right: ${({ $centerPadRight }) => `${$centerPadRight}px`};
  z-index: ${({ $zIndex }) => $zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $dimmed }) =>
    $dimmed
      ? (theme.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.25)')
      : 'transparent'};
  pointer-events: ${({ $passthrough }) => ($passthrough ? 'none' : 'auto')};
  animation: ${overlayFadeIn} 0.2s ease;
`;

const Positioner = styled.div<{ $x: number; $y: number }>`
  transform: translate(${({ $x }) => $x}px, ${({ $y }) => $y}px);
`;

const ModalContainer = styled.div<{
  $width?: string;
  $height?: string;
  $bgOpacity: number;
  $centerPadLeft: number;
  $centerPadRight: number;
  $mountMode: 'viewport' | 'parent';
}>`
  position: relative;
  pointer-events: auto;
  width: ${({ $width }) => $width || '520px'};
  ${({ $height }) => $height && css`height: ${$height};`}
  max-width: ${({ $centerPadLeft, $centerPadRight, $mountMode }) =>
    $mountMode === 'parent'
      ? `max(280px, calc(100% - 32px - ${$centerPadLeft}px - ${$centerPadRight}px))`
      : `max(280px, calc(100vw - 32px - ${$centerPadLeft}px - ${$centerPadRight}px))`};
  max-height: ${({ $mountMode }) => ($mountMode === 'parent' ? 'calc(100% - 64px)' : 'calc(100dvh - 64px)')};
  display: flex;
  flex-direction: column;
  background: ${({ theme, $bgOpacity }) => toRgbaWithAlpha(theme.colors.bgPrimary, $bgOpacity)};
  border: 0.5px solid ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'};
  border-radius: 16px;
  box-shadow: ${({ theme }) =>
    theme.mode === 'dark'
      ? '0 24px 80px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.2)'
      : '0 24px 80px rgba(0,0,0,0.12), 0 8px 24px rgba(0,0,0,0.08)'};
  animation: ${modalSlideIn} 0.25s cubic-bezier(0.22, 1, 0.36, 1);
  overflow: hidden;
`;

const Header = styled.div<{ $draggable?: boolean; $pinned?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  flex-shrink: 0;
  cursor: ${({ $draggable, $pinned }) => ($draggable && !$pinned ? 'grab' : 'default')};

  &:active {
    cursor: ${({ $draggable, $pinned }) => ($draggable && !$pinned ? 'grabbing' : 'default')};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.modal.titleFontSize};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.modalMobile.titleFontSize};
  }
`;

const TitleSuffix = styled.div`
  display: flex;
  align-items: center;
`;

const CloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const OpacityControl = styled.div`
  display: flex;
  align-items: center;
  padding: 0 2px;
`;

const OpacityRange = styled.input`
  width: 76px;
  height: 18px;
  margin: 0;
  background: transparent;
  appearance: none;
  -webkit-appearance: none;
  cursor: ew-resize;

  &::-webkit-slider-runnable-track {
    height: 3px;
    border-radius: 999px;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.18)'};
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 11px;
    height: 11px;
    border-radius: 50%;
    margin-top: -4px;
    border: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.2)'};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.96)'};
  }

  &::-moz-range-track {
    height: 3px;
    border-radius: 999px;
    border: none;
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.26)' : 'rgba(0,0,0,0.18)'};
  }

  &::-moz-range-thumb {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1px solid ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.2)'};
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.96)'};
  }
`;

const PinBtn = styled.button<{ $active?: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, $active }) => ($active ? theme.colors.textPrimary : theme.colors.textMuted)};
  background: transparent;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) =>
      theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)'};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
`;

const Body = styled.div<{
  $resizable?: boolean;
  $padding: string;
  $overflowY: 'auto' | 'hidden' | 'visible';
  $hideScrollbar: boolean;
}>`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: ${({ $overflowY }) => $overflowY};
  padding: ${({ $padding }) => $padding};

  ${({ $hideScrollbar }) => $hideScrollbar && hideScrollbarCss}

  ${({ $resizable }) =>
    $resizable &&
    css`
      display: flex;
      flex-direction: column;
    `}
`;

/* ------------------------------------------------------------------ */
/* Resize Handles                                                      */
/* ------------------------------------------------------------------ */

const HANDLE_EDGE = 6;
const HANDLE_CORNER = 14;

const ResizeHandle = styled.div<{ $dir: ResizeDir }>`
  position: absolute;
  z-index: 10;

  ${({ $dir }) => {
    switch ($dir) {
      case 'n':
        return css`top: 0; left: ${HANDLE_CORNER}px; right: ${HANDLE_CORNER}px; height: ${HANDLE_EDGE}px; cursor: ns-resize;`;
      case 's':
        return css`bottom: 0; left: ${HANDLE_CORNER}px; right: ${HANDLE_CORNER}px; height: ${HANDLE_EDGE}px; cursor: ns-resize;`;
      case 'e':
        return css`right: 0; top: ${HANDLE_CORNER}px; bottom: ${HANDLE_CORNER}px; width: ${HANDLE_EDGE}px; cursor: ew-resize;`;
      case 'w':
        return css`left: 0; top: ${HANDLE_CORNER}px; bottom: ${HANDLE_CORNER}px; width: ${HANDLE_EDGE}px; cursor: ew-resize;`;
      case 'ne':
        return css`top: 0; right: 0; width: ${HANDLE_CORNER}px; height: ${HANDLE_CORNER}px; cursor: nesw-resize;`;
      case 'nw':
        return css`top: 0; left: 0; width: ${HANDLE_CORNER}px; height: ${HANDLE_CORNER}px; cursor: nwse-resize;`;
      case 'se':
        return css`bottom: 0; right: 0; width: ${HANDLE_CORNER}px; height: ${HANDLE_CORNER}px; cursor: nwse-resize;`;
      case 'sw':
        return css`bottom: 0; left: 0; width: ${HANDLE_CORNER}px; height: ${HANDLE_CORNER}px; cursor: nesw-resize;`;
    }
  }}
`;

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  titleIcon?: React.ReactNode;
  titleSuffix?: React.ReactNode;
  width?: string;
  children: React.ReactNode;
  resizable?: boolean;
  defaultHeight?: number;
  minWidth?: number;
  minHeight?: number;
  draggable?: boolean;
  pinnable?: boolean;
  defaultPinned?: boolean;
  initialOffsetX?: number;
  initialOffsetY?: number;
  opacityAdjustable?: boolean;
  defaultOpacity?: number;
  minOpacityPercent?: number;
  closeOnBackdrop?: boolean;
  dimBackdrop?: boolean;
  passthroughBackdrop?: boolean;
  onSizeChange?: (size: { w: number; h: number }) => void;
  onOffsetChange?: (offset: { x: number; y: number }) => void;
  onPinnedChange?: (pinned: boolean) => void;
  onOpacityChange?: (opacity: number) => void;
  centerPaddingLeft?: number;
  centerPaddingRight?: number;
  mountMode?: 'viewport' | 'parent';
  hostBounds?: { width: number; height: number };
  bodyPadding?: string;
  bodyOverflowY?: 'auto' | 'hidden' | 'visible';
  hideBodyScrollbar?: boolean;
  layerZIndex?: number;
  onActivate?: () => void;
}

function parsePx(val: string | undefined, fallback: number): number {
  if (!val) return fallback;
  const n = parseInt(val, 10);
  return isNaN(n) ? fallback : n;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  titleIcon,
  titleSuffix,
  width,
  children,
  resizable = false,
  defaultHeight,
  minWidth = 320,
  minHeight = 200,
  draggable = false,
  pinnable = false,
  defaultPinned = false,
  initialOffsetX = 0,
  initialOffsetY = 0,
  opacityAdjustable = false,
  defaultOpacity = 1,
  minOpacityPercent = OPACITY_MIN,
  closeOnBackdrop = true,
  dimBackdrop = true,
  passthroughBackdrop = false,
  onSizeChange,
  onOffsetChange,
  onPinnedChange,
  onOpacityChange,
  centerPaddingLeft = 0,
  centerPaddingRight = 0,
  mountMode = 'viewport',
  hostBounds,
  bodyPadding = '0 24px 24px',
  bodyOverflowY = 'auto',
  hideBodyScrollbar = false,
  layerZIndex = 100,
  onActivate,
}) => {
  const overlayPointerDownRef = useRef(false);
  const overlayPointerIdRef = useRef<number | null>(null);
  const wasOpenRef = useRef(false);
  const safeCenterPaddingLeft = Math.max(0, Number.isFinite(centerPaddingLeft) ? centerPaddingLeft : 0);
  const safeCenterPaddingRight = Math.max(0, Number.isFinite(centerPaddingRight) ? centerPaddingRight : 0);
  const safeMinOpacityPercent = Math.min(
    OPACITY_MAX,
    Math.max(
      OPACITY_MIN,
      Number.isFinite(minOpacityPercent) ? Math.round(minOpacityPercent) : OPACITY_MIN,
    ),
  );
  const minOpacityAlpha = safeMinOpacityPercent / 100;
  const safeHostWidth = hostBounds && Number.isFinite(hostBounds.width)
    ? Math.max(320, Math.round(hostBounds.width))
    : null;
  const safeHostHeight = hostBounds && Number.isFinite(hostBounds.height)
    ? Math.max(200, Math.round(hostBounds.height))
    : null;

  const initW = parsePx(width, 520);
  const initH = defaultHeight || 400;

  const [size, setSize] = useState({ w: initW, h: initH });
  const sizeRef = useRef(size);
  sizeRef.current = size;
  const [offset, setOffset] = useState({ x: initialOffsetX, y: initialOffsetY });
  const offsetRef = useRef(offset);
  offsetRef.current = offset;
  const [pinned, setPinned] = useState(defaultPinned);
  const [bgOpacity, setBgOpacity] = useState(Math.min(1, Math.max(minOpacityAlpha, defaultOpacity)));

  const effectiveSizeRef = useRef({ w: initW, h: initH });
  effectiveSizeRef.current = {
    w: resizable ? size.w : initW,
    h: resizable ? size.h : initH,
  };

  const dragRef = useRef<{
    dir: ResizeDir;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);
  const moveRef = useRef<{
    startX: number;
    startY: number;
    baseX: number;
    baseY: number;
  } | null>(null);

  const getAvailableWidth = useCallback(
    () => Math.max(
      320,
      (safeHostWidth ?? window.innerWidth) - safeCenterPaddingLeft - safeCenterPaddingRight,
    ),
    [safeCenterPaddingLeft, safeCenterPaddingRight, safeHostWidth],
  );

  const getAvailableHeight = useCallback(
    () => Math.max(200, safeHostHeight ?? window.innerHeight),
    [safeHostHeight],
  );

  const clampOffset = useCallback((x: number, y: number, w: number, h: number) => {
    const availableW = getAvailableWidth();
    const availableH = getAvailableHeight();
    const effectiveW = Math.min(w, availableW - 32);
    const effectiveH = Math.min(h, availableH - 64);
    const maxX = Math.max(0, (availableW - effectiveW) / 2);
    const maxY = Math.max(0, (availableH - effectiveH) / 2);
    return {
      x: Math.min(maxX, Math.max(-maxX, x)),
      y: Math.min(maxY, Math.max(-maxY, y)),
    };
  }, [getAvailableHeight, getAvailableWidth]);

  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    if (justOpened) {
      if (resizable) {
        setSize({ w: initW, h: initH });
      }
      if (draggable) {
        const initial = clampOffset(
          initialOffsetX,
          initialOffsetY,
          initW,
          initH,
        );
        setOffset(initial);
      } else {
        setOffset({ x: 0, y: 0 });
      }
      setPinned(pinnable ? defaultPinned : false);
      setBgOpacity(Math.min(1, Math.max(minOpacityAlpha, defaultOpacity)));
    }
    wasOpenRef.current = open;
  }, [
    open,
    resizable,
    draggable,
    pinnable,
    defaultPinned,
    initW,
    initH,
    initialOffsetX,
    initialOffsetY,
    clampOffset,
    defaultOpacity,
    minOpacityAlpha,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open, handleKeyDown]);

  useEffect(() => {
    if (!open || !draggable) return;

    const clampCurrent = () => {
      const clamped = clampOffset(
        offsetRef.current.x,
        offsetRef.current.y,
        effectiveSizeRef.current.w,
        effectiveSizeRef.current.h,
      );
      if (clamped.x !== offsetRef.current.x || clamped.y !== offsetRef.current.y) {
        setOffset(clamped);
      }
    };

    clampCurrent();
    window.addEventListener('resize', clampCurrent);
    return () => window.removeEventListener('resize', clampCurrent);
  }, [open, draggable, clampOffset, size, safeHostWidth, safeHostHeight]);

  useEffect(() => {
    if (!open || !pinnable || !onPinnedChange) return;
    onPinnedChange(pinned);
  }, [open, pinnable, pinned, onPinnedChange]);

  useEffect(() => {
    if (!open || !opacityAdjustable || !onOpacityChange) return;
    onOpacityChange(bgOpacity);
  }, [open, opacityAdjustable, bgOpacity, onOpacityChange]);

  /* ---- Resize pointer tracking ---- */

  useEffect(() => {
    if (!resizable || !open) return;

    const onMove = (e: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;

      let newW = drag.startW;
      let newH = drag.startH;

      if (drag.dir.includes('e')) newW = drag.startW + dx;
      if (drag.dir.includes('w')) newW = drag.startW - dx;
      if (drag.dir.includes('s')) newH = drag.startH + dy;
      if (drag.dir.includes('n')) newH = drag.startH - dy;

      const nextSize = {
        w: Math.min(getAvailableWidth() - 32, Math.max(minWidth, newW)),
        h: Math.min(getAvailableHeight() - 64, Math.max(minHeight, newH)),
      };
      const widthDelta = nextSize.w - drag.startW;
      const heightDelta = nextSize.h - drag.startH;

      let nextOffsetX = drag.startOffsetX;
      let nextOffsetY = drag.startOffsetY;

      if (drag.dir.includes('e')) nextOffsetX = drag.startOffsetX + widthDelta / 2;
      if (drag.dir.includes('w')) nextOffsetX = drag.startOffsetX - widthDelta / 2;
      if (drag.dir.includes('s')) nextOffsetY = drag.startOffsetY + heightDelta / 2;
      if (drag.dir.includes('n')) nextOffsetY = drag.startOffsetY - heightDelta / 2;

      const nextOffset = clampOffset(nextOffsetX, nextOffsetY, nextSize.w, nextSize.h);
      setSize(nextSize);
      setOffset(nextOffset);
    };

    const onUp = () => {
      const didResize = !!dragRef.current;
      if (didResize) {
        dragRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        onSizeChange?.(sizeRef.current);
        onOffsetChange?.(offsetRef.current);
      }
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      onUp();
    };
  }, [
    resizable,
    open,
    minWidth,
    minHeight,
    onSizeChange,
    onOffsetChange,
    getAvailableWidth,
    getAvailableHeight,
    clampOffset,
  ]);

  useEffect(() => {
    if (!open || !draggable) return;

    const onMove = (e: PointerEvent) => {
      const move = moveRef.current;
      if (!move) return;
      const next = clampOffset(
        move.baseX + (e.clientX - move.startX),
        move.baseY + (e.clientY - move.startY),
        effectiveSizeRef.current.w,
        effectiveSizeRef.current.h,
      );
      setOffset(next);
    };

    const onUp = () => {
      const didMove = !!moveRef.current;
      if (didMove) {
        moveRef.current = null;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        onOffsetChange?.(offsetRef.current);
      }
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      onUp();
    };
  }, [open, draggable, clampOffset, onOffsetChange]);

  const startResize = useCallback(
    (dir: ResizeDir, e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = {
        dir,
        startX: e.clientX,
        startY: e.clientY,
        startW: sizeRef.current.w,
        startH: sizeRef.current.h,
        startOffsetX: offsetRef.current.x,
        startOffsetY: offsetRef.current.y,
      };
      document.body.style.cursor = CURSOR_MAP[dir];
      document.body.style.userSelect = 'none';
    },
    [],
  );

  const startMove = useCallback((e: React.PointerEvent) => {
    if (!draggable || pinned) return;
    moveRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseX: offsetRef.current.x,
      baseY: offsetRef.current.y,
    };
    document.body.style.cursor = 'grabbing';
    document.body.style.userSelect = 'none';
  }, [draggable, pinned]);

  if (!open) return null;

  const containerWidth = resizable ? `${size.w}px` : width;
  const containerHeight = resizable ? `${size.h}px` : undefined;
  const opacityPercent = Math.round(Math.min(1, Math.max(minOpacityAlpha, bgOpacity)) * 100);

  const modal = (
    <Overlay
      $dimmed={dimBackdrop}
      $passthrough={passthroughBackdrop}
      $centerPadLeft={safeCenterPaddingLeft}
      $centerPadRight={safeCenterPaddingRight}
      $mountMode={mountMode}
      $zIndex={layerZIndex}
      onPointerDown={(e) => {
        overlayPointerDownRef.current = e.target === e.currentTarget;
        overlayPointerIdRef.current = e.pointerId;
      }}
      onPointerUp={(e) => {
        const endedOnOverlay = e.target === e.currentTarget;
        const samePointer = overlayPointerIdRef.current === e.pointerId;
        if (closeOnBackdrop && overlayPointerDownRef.current && endedOnOverlay && samePointer) {
          onClose();
        }
        overlayPointerDownRef.current = false;
        overlayPointerIdRef.current = null;
      }}
      onPointerCancel={() => {
        overlayPointerDownRef.current = false;
        overlayPointerIdRef.current = null;
      }}
    >
      <Positioner $x={draggable ? offset.x : 0} $y={draggable ? offset.y : 0}>
        <ModalContainer
          $width={containerWidth}
          $height={containerHeight}
          $bgOpacity={Math.min(1, Math.max(minOpacityAlpha, bgOpacity))}
          $centerPadLeft={safeCenterPaddingLeft}
          $centerPadRight={safeCenterPaddingRight}
          $mountMode={mountMode}
          onPointerDownCapture={() => {
            onActivate?.();
          }}
          onPointerDown={() => {
            overlayPointerDownRef.current = false;
            overlayPointerIdRef.current = null;
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header
            $draggable={draggable}
            $pinned={pinned}
            onPointerDown={startMove}
          >
            <TitleRow>
              {titleIcon}
              <Title>{title}</Title>
              {titleSuffix && (
                <TitleSuffix onPointerDown={(e) => e.stopPropagation()}>
                  {titleSuffix}
                </TitleSuffix>
              )}
            </TitleRow>
            <HeaderActions>
              {opacityAdjustable && (
                <OpacityControl onPointerDown={(e) => e.stopPropagation()}>
                  <OpacityRange
                    type="range"
                    min={safeMinOpacityPercent}
                    max={OPACITY_MAX}
                    step={5}
                    value={opacityPercent}
                    title={`투명도 ${opacityPercent}%`}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      const normalized = Math.min(OPACITY_MAX, Math.max(safeMinOpacityPercent, value));
                      setBgOpacity(normalized / 100);
                    }}
                  />
                </OpacityControl>
              )}
              {pinnable && (
                <PinBtn
                  $active={pinned}
                  title={pinned ? '고정 해제' : '위치 고정'}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPinned((prev) => !prev);
                  }}
                >
                  {pinned ? <PinOff size={14} /> : <Pin size={14} />}
                </PinBtn>
              )}
              <CloseBtn onPointerDown={(e) => e.stopPropagation()} onClick={onClose}>
                <X size={18} />
              </CloseBtn>
            </HeaderActions>
          </Header>
          <Body
            $resizable={resizable}
            $padding={bodyPadding}
            $overflowY={bodyOverflowY}
            $hideScrollbar={hideBodyScrollbar}
          >
            {children}
          </Body>
          {resizable &&
            RESIZE_DIRS.map((dir) => (
              <ResizeHandle
                key={dir}
                $dir={dir}
                onPointerDown={(e) => startResize(dir, e)}
              />
            ))}
        </ModalContainer>
      </Positioner>
    </Overlay>
  );

  if (mountMode === 'viewport' && typeof document !== 'undefined') {
    return ReactDOM.createPortal(modal, document.body);
  }

  return modal;
};
